import { ActionIcon, Divider, Group, Loader, Tooltip } from '@mantine/core';
import {
  IconArrowBackUp,
  IconCalendarPlus,
  IconEdit,
  IconEyeCheck,
  IconEyeOff,
  IconTrash,
  IconTrashOff,
} from '@tabler/icons';
import { useContext, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useDisclosure } from '@mantine/hooks';
import { EmailBoxContext } from '../../Service/Context/EmailBoxContext';
import { MessageS } from '../../Service/MessageS';
import { getCustomerId } from '../../Service/restapi/contactService';
import { saveEventParticipation } from '../../Service/restapi/eventService';
import { ModalDialog } from '../Dialogs';
import EventSearchAutocomplete from '../../Organismns/Event/EventSearchAutocomplete';
import { TransButtonDefault, TransButtonPrimaryWithSpinner } from '../../Atoms/Buttons';
import { openCreateTemplateModal } from '../../Organismns/Message/MessageModals';
import { ViolationContext } from '../../Service/Context/ViolationContext';
import { LicenceS } from '../../Service/LicenceS';
import { openDeleteConfirmModal } from '../../Atoms/DeleteConfirmModal';
import { folderActionIconSx } from '../../Organismns/Message/EmailFolderActions';

const MessageActions = () => {
  const { selectedMessage, changeEmail, prepareMessage, refresh, changeSeenFlag, changingSeenFlag, openedFolder } =
    useContext(EmailBoxContext);
  const { box } = useParams();
  const [opened, { toggle }] = useDisclosure(false);
  const [selectedEventId, setSelectedEventId] = useState(undefined);
  const { executeWithLicenceCheck } = useContext(ViolationContext);

  if (!selectedMessage) {
    return null;
  }
  const { id, readTimestamp, localDateTime, imap } = selectedMessage;

  const deleteHandler = () => {
    openDeleteConfirmModal({
      title: 'Nachricht endgültig löschen?',
      description: 'Möchtest Du diese Nachricht wirklich endgültig löschen?',
      onConfirm: async () => {
        const deleted = await MessageS.deleteEmail(id);
        if (deleted) {
          refresh();
          return true;
        }
        return false;
      },
    });
  };

  const changeTrashFlag = async (trash: boolean) => {
    const email = await MessageS.patchEmail(id, { trash });
    if (email) {
      refresh();
      return true;
    }
    return false;
  };

  const trashHandler = async (trash: boolean) => {
    if (!trash) {
      await changeTrashFlag(false);
      return;
    }

    openDeleteConfirmModal({
      title: 'Nachricht löschen?',
      description: 'Möchtest Du diese Nachricht wirklich in den Papierkorb verschieben?',
      onConfirm: () => changeTrashFlag(true),
    });
  };

  const assignContactToEvent = async () => {
    const patchedEmail = await MessageS.patchEmail(id, { eventId: selectedEventId });
    if (patchedEmail) {
      changeEmail(patchedEmail);
      toggle();
    }

    let participation: any = {
      eventId: selectedEventId,
      eventRole: 'CUSTOMER',
    };
    const customerId = getCustomerId(selectedMessage);

    if (customerId) {
      participation = {
        ...participation,
        contactId: customerId,
      };
    }
    saveEventParticipation(participation);
  };

  const createTemplate = () =>
    executeWithLicenceCheck(LicenceS.Restrictions.MULTIPLE_EMAIL_TEMPLATE, () =>
      openCreateTemplateModal({ messageId: id }),
    );

  const showReply = openedFolder && openedFolder.name.toLowerCase() === 'inbox';
  const showEdit = ['scheduled', 'failed', 'drafts'].includes(box ?? '') && !localDateTime;
  const showMarkAsRead = !changingSeenFlag && !readTimestamp && (imap || showReply);
  const showMarkAsUnread = !changingSeenFlag && readTimestamp && (imap || showReply);

  return (
    <>
      <Group
        py={5}
        px="sm"
        spacing="xs"
        sx={(theme) => ({
          backgroundColor: theme.colorScheme === 'dark' ? theme.colors.dark[6] : theme.colors.gray[0],
          borderRadius: 25,
        })}
      >
        {showReply && (
          <>
            <Tooltip openDelay={300} withArrow transition="slide-down" label="Antwort" position="bottom">
              <ActionIcon size="sm" sx={folderActionIconSx} onClick={() => prepareMessage('REPLY')}>
                <IconArrowBackUp />
              </ActionIcon>
            </Tooltip>
            <Divider orientation="vertical" />
          </>
        )}
        {showMarkAsRead && (
          <Tooltip openDelay={300} withArrow transition="slide-down" label="als gelesen markieren" position="bottom">
            <ActionIcon size="sm" sx={folderActionIconSx} onClick={() => changeSeenFlag(true)}>
              <IconEyeCheck />
            </ActionIcon>
          </Tooltip>
        )}
        {showMarkAsUnread && (
          <Tooltip openDelay={300} withArrow transition="slide-down" label="als unerledigt Markieren" position="bottom">
            <ActionIcon size="sm" sx={folderActionIconSx} onClick={() => changeSeenFlag(false)}>
              <IconEyeOff />
            </ActionIcon>
          </Tooltip>
        )}
        {changingSeenFlag && <Loader size="xs" />}

        {showEdit && !imap && (
          <>
            <Tooltip openDelay={300} withArrow transition="slide-down" label="Bearbeiten" position="bottom">
              <ActionIcon size="sm" sx={folderActionIconSx} onClick={() => prepareMessage('EDIT')}>
                <IconEdit />
              </ActionIcon>
            </Tooltip>
            <Divider orientation="vertical" />
          </>
        )}
        {!selectedMessage.eventId && !imap && (
          <>
            {box !== 'trash' && box !== 'drafts' && box !== 'outbound' && <Divider orientation="vertical" />}
            <Tooltip openDelay={300} withArrow transition="slide-down" label="Event zuweisen" position="bottom">
              <ActionIcon size="sm" sx={folderActionIconSx} onClick={toggle}>
                <IconCalendarPlus />
              </ActionIcon>
            </Tooltip>
            <Divider orientation="vertical" />
          </>
        )}
        {box === 'trash' && !imap && (
          <>
            <Tooltip openDelay={300} withArrow transition="slide-down" label="Wiederherstellen" position="bottom">
              <ActionIcon size="sm" sx={folderActionIconSx} onClick={() => trashHandler(false)}>
                <IconTrashOff />
              </ActionIcon>
            </Tooltip>
            <Divider orientation="vertical" />
            <Tooltip openDelay={300} withArrow transition="slide-down" label="Endgültig löschen" position="bottom">
              <ActionIcon size="sm" sx={folderActionIconSx} onClick={deleteHandler}>
                <IconTrash />
              </ActionIcon>
            </Tooltip>
          </>
        )}
        {box !== 'trash' && !imap && (
          <>
            {box === 'inbox' && <Divider orientation="vertical" />}
            <Tooltip openDelay={300} withArrow transition="slide-down" label="Löschen" position="bottom">
              <ActionIcon size="sm" sx={folderActionIconSx} onClick={() => trashHandler(true)}>
                <IconTrash />
              </ActionIcon>
            </Tooltip>
          </>
        )}
      </Group>
      <ModalDialog
        header="Event zuweisen"
        body={<EventSearchAutocomplete selectEvent={(bookingEvent: any) => setSelectedEventId(bookingEvent.id)} />}
        footer={
          <div>
            <TransButtonPrimaryWithSpinner text="actions.save" icon="SAVE" onClickHandler={assignContactToEvent} />{' '}
            <TransButtonDefault onClickHandler={toggle} text="actions.close" />
          </div>
        }
        show={opened}
        toggle={toggle}
      />
    </>
  );
};

export default MessageActions;
